import { useState, useCallback } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://diaperstops-backend.fly.dev';

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

export interface UploadResult {
  success: boolean;
  publicUrl?: string;
  key?: string;
  error?: string;
}

export interface UseImageUploadReturn {
  uploading: boolean;
  progress: UploadProgress | null;
  error: string | null;
  uploadImage: (file: File, category?: string) => Promise<UploadResult>;
  reset: () => void;
}

/**
 * Hook for uploading images to Backblaze B2 or Cloudflare R2
 * Supports direct upload via presigned URLs
 */
export function useImageUpload(): UseImageUploadReturn {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setUploading(false);
    setProgress(null);
    setError(null);
  }, []);

  const uploadImage = useCallback(async (
    file: File,
    category: string = 'general'
  ): Promise<UploadResult> => {
    setUploading(true);
    setError(null);
    setProgress({ loaded: 0, total: file.size, percentage: 0 });

    try {
      // Validate file
      if (!file.type.startsWith('image/')) {
        throw new Error('File must be an image');
      }

      if (file.size > 10 * 1024 * 1024) {
        throw new Error('File size must be less than 10MB');
      }

      const token = localStorage.getItem('token');

      // Step 1: Get presigned upload URL
      setProgress({ loaded: 0, total: file.size, percentage: 10 });
      
      const urlResponse = await fetch(`${API_BASE_URL}/api/images/upload-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          contentType: file.type,
          category,
        }),
      });

      if (!urlResponse.ok) {
        const errorData = await urlResponse.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to get upload URL');
      }

      const { uploadUrl, key, publicUrl } = await urlResponse.json();

      // Step 2: Upload directly to storage (B2/R2)
      setProgress({ loaded: 0, total: file.size, percentage: 30 });

      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type,
        },
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file to storage');
      }

      setProgress({ loaded: file.size, total: file.size, percentage: 80 });

      // Step 3: Confirm upload with backend
      const confirmResponse = await fetch(`${API_BASE_URL}/api/images/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ key }),
      });

      if (!confirmResponse.ok) {
        throw new Error('Failed to confirm upload');
      }

      const confirmData = await confirmResponse.json();
      setProgress({ loaded: file.size, total: file.size, percentage: 100 });

      return {
        success: true,
        publicUrl: confirmData.publicUrl || publicUrl,
        key,
      };

    } catch (err: any) {
      const errorMessage = err.message || 'Upload failed';
      setError(errorMessage);
      return {
        success: false,
        error: errorMessage,
      };
    } finally {
      setUploading(false);
    }
  }, []);

  return {
    uploading,
    progress,
    error,
    uploadImage,
    reset,
  };
}

/**
 * Hook for uploading multiple images
 */
export function useMultipleImageUpload() {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<UploadResult[]>([]);

  const uploadImages = useCallback(async (
    files: File[],
    category: string = 'gallery'
  ): Promise<UploadResult[]> => {
    setUploading(true);
    setError(null);
    setProgress(0);
    setResults([]);

    const uploadResults: UploadResult[] = [];
    const token = localStorage.getItem('token');

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      try {
        // Validate
        if (!file.type.startsWith('image/')) {
          uploadResults.push({
            success: false,
            error: `${file.name}: Not an image file`,
          });
          continue;
        }

        if (file.size > 10 * 1024 * 1024) {
          uploadResults.push({
            success: false,
            error: `${file.name}: File too large (max 10MB)`,
          });
          continue;
        }

        // Get upload URL
        const urlResponse = await fetch(`${API_BASE_URL}/api/images/upload-url`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            contentType: file.type,
            category,
          }),
        });

        if (!urlResponse.ok) {
          throw new Error('Failed to get upload URL');
        }

        const { uploadUrl, key, publicUrl } = await urlResponse.json();

        // Upload to storage
        const uploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': file.type,
          },
          body: file,
        });

        if (!uploadResponse.ok) {
          throw new Error('Upload failed');
        }

        // Confirm
        const confirmResponse = await fetch(`${API_BASE_URL}/api/images/confirm`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ key }),
        });

        if (!confirmResponse.ok) {
          throw new Error('Confirmation failed');
        }

        const confirmData = await confirmResponse.json();

        uploadResults.push({
          success: true,
          publicUrl: confirmData.publicUrl || publicUrl,
          key,
        });

      } catch (err: any) {
        uploadResults.push({
          success: false,
          error: `${file.name}: ${err.message}`,
        });
      }

      setProgress(((i + 1) / files.length) * 100);
      setResults([...uploadResults]);
    }

    setUploading(false);
    return uploadResults;
  }, []);

  const reset = useCallback(() => {
    setUploading(false);
    setProgress(0);
    setError(null);
    setResults([]);
  }, []);

  return {
    uploading,
    progress,
    error,
    results,
    uploadImages,
    reset,
  };
}
